import React, { useState } from 'react';
import { useGame } from '../context/GameContext';

const RESOURCE_GROUPS = [
  {
    label: 'CURRENCY & ENERGY',
    icon: '₢',
    color: '#22c55e',
    resources: [
      { key: 'credits', label: 'Credits', icon: '₢' },
      { key: 'energy', label: 'Energy', icon: '⚡' },
    ],
  },
  {
    label: 'RAW',
    icon: '⛏',
    color: '#f59e0b',
    resources: [
      { key: 'iron_ore', label: 'Iron Ore', icon: '⛏' },
      { key: 'copper_ore', label: 'Copper Ore', icon: '⛏' },
      { key: 'titanium_ore', label: 'Titanium Ore', icon: '⛏' },
      { key: 'tungsten_ore', label: 'Tungsten Ore', icon: '⛏' },
      { key: 'silicon', label: 'Silicon', icon: '◇' },
      { key: 'rare_crystals', label: 'Rare Crystals', icon: '◆' },
      { key: 'quantum_dust', label: 'Quantum Dust', icon: '✦' },
      { key: 'ice', label: 'Ice', icon: '❄' },
      { key: 'hydrogen_gas', label: 'Hydrogen Gas', icon: '☁' },
      { key: 'methane_gas', label: 'Methane Gas', icon: '☁' },
      { key: 'carbon', label: 'Carbon', icon: '●' },
      { key: 'biomass', label: 'Biomass', icon: '🌿' },
    ],
  },
  {
    label: 'REFINED',
    icon: '🔩',
    color: '#3b82f6',
    resources: [
      { key: 'iron_plate', label: 'Iron Plate', icon: '🔩' },
      { key: 'copper_plate', label: 'Copper Plate', icon: '🔩' },
      { key: 'titanium_plate', label: 'Titanium Plate', icon: '🔩' },
      { key: 'tungsten_plate', label: 'Tungsten Plate', icon: '🔩' },
      { key: 'silicon_wafer', label: 'Silicon Wafer', icon: '◇' },
      { key: 'crystal_core', label: 'Crystal Core', icon: '◆' },
      { key: 'quantum_substrate', label: 'Quantum Substrate', icon: '✦' },
      { key: 'water', label: 'Water', icon: '💧' },
      { key: 'refined_gas', label: 'Refined Gas', icon: '☁' },
      { key: 'polymer_base', label: 'Polymer Base', icon: '⬡' },
      { key: 'bio_gel', label: 'Bio Gel', icon: '⬡' },
      { key: 'carbon_block', label: 'Carbon Block', icon: '▪' },
    ],
  },
  {
    label: 'ALLOYS',
    icon: '⚗',
    color: '#a855f7',
    resources: [
      { key: 'steel', label: 'Steel', icon: '⚗' },
      { key: 'heavy_alloy', label: 'Heavy Alloy', icon: '⚗' },
      { key: 'reinforced_alloy', label: 'Reinforced Alloy', icon: '⚗' },
      { key: 'basic_circuit', label: 'Basic Circuit', icon: '⚡' },
      { key: 'advanced_circuit', label: 'Advanced Circuit', icon: '⚡' },
      { key: 'quantum_processor', label: 'Quantum Processor', icon: '⚡' },
      { key: 'fuel_cell', label: 'Fuel Cell', icon: '⬡' },
      { key: 'high_density_fuel', label: 'High-Density Fuel', icon: '⬡' },
      { key: 'bio_fuel', label: 'Bio Fuel', icon: '⬡' },
      { key: 'industrial_polymer', label: 'Industrial Polymer', icon: '⬡' },
      { key: 'composite_material', label: 'Composite Material', icon: '⬡' },
    ],
  },
  {
    label: 'PARTS',
    icon: '⚙',
    color: '#ec4899',
    resources: [
      { key: 'hull_segment', label: 'Hull Segment', icon: '⚙' },
      { key: 'structural_frame', label: 'Structural Frame', icon: '⚙' },
      { key: 'engine_core', label: 'Engine Core', icon: '⚙' },
      { key: 'thruster', label: 'Thruster', icon: '⚙' },
      { key: 'weapon_mount', label: 'Weapon Mount', icon: '⚙' },
      { key: 'ai_core', label: 'AI Core', icon: '⚙' },
      { key: 'control_unit', label: 'Control Unit', icon: '⚙' },
      { key: 'sensor_array', label: 'Sensor Array', icon: '⚙' },
      { key: 'power_core', label: 'Power Core', icon: '⚙' },
      { key: 'reactor_unit', label: 'Reactor Unit', icon: '⚙' },
      { key: 'shield_generator', label: 'Shield Generator', icon: '⚙' },
    ],
  },
  {
    label: 'MODULES',
    icon: '🔧',
    color: '#14b8a6',
    resources: [
      { key: 'engine_module', label: 'Engine Module', icon: '🔧' },
      { key: 'hull_section', label: 'Hull Section', icon: '🔧' },
      { key: 'energy_system', label: 'Energy System', icon: '🔧' },
      { key: 'ai_system', label: 'AI System', icon: '🔧' },
    ],
  },
  {
    label: 'SHIPS',
    icon: '🚀',
    color: '#f43f5e',
    resources: [
      { key: 'ship_mining_item', label: 'Mining Ship', icon: '🚀' },
      { key: 'ship_freighter_item', label: 'Freighter', icon: '🚀' },
      { key: 'ship_capital_freighter_item', label: 'Capital Freighter', icon: '🚀' },
      { key: 'ship_cruiser_item', label: 'Cruiser', icon: '🚀' },
      { key: 'ship_battleship_item', label: 'Battleship', icon: '🚀' },
    ],
  },
];

function InventoryPanel() {
  const { inventory } = useGame();
  const [collapsed, setCollapsed] = useState({});

  if (!inventory) return <div className="panel"><p className="hud-empty">Loading cargo manifest...</p></div>;

  const fmtNum = (n) => Number(n || 0).toLocaleString();
  const toggle = (label) => setCollapsed(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="panel inventory-panel">
      <h3 className="section-title">
        <span className="title-accent">▣</span> CARGO MANIFEST
      </h3>
      {RESOURCE_GROUPS.map(group => {
        const isCollapsed = collapsed[group.label];
        const hasAny = group.resources.some(r => Number(inventory[r.key] || 0) > 0);
        return (
          <section key={group.label} className="section inventory-group">
            <div
              className="inventory-group-header"
              style={{ '--group-color': group.color }}
              onClick={() => toggle(group.label)}
            >
              <span className="group-icon" style={{ color: group.color }}>{group.icon}</span>
              <span className="group-label">{group.label}</span>
              {hasAny && <span className="group-has-items">●</span>}
              <span className="group-toggle">{isCollapsed ? '▶' : '▼'}</span>
            </div>
            {!isCollapsed && (
              <div className="hud-grid inventory-grid">
                {group.resources.map(r => {
                  const val = Number(inventory[r.key] || 0);
                  return (
                    <div
                      key={r.key}
                      className={`hud-card resource-card ${val > 0 ? 'has-stock' : 'empty-stock'}`}
                      style={{ '--res-color': group.color }}
                    >
                      <div className="resource-icon-lg" style={{ color: group.color }}>{r.icon}</div>
                      <div className="resource-info">
                        <div className="resource-label">{r.label}</div>
                        <div className="resource-amount">{fmtNum(val)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default InventoryPanel;
